import { api } from "../../../lib/api";
import type { EmailCampaign, EmailQuota } from "../../../types/marketing";
export interface EmailPage<T> {
  results: T[];
  count: number;
  next: string | null;
}
export interface AudienceSegment {
  id: number;
  name: string;
  audience_type: "all" | "tags" | "contact_type" | "manual";
  audience_contact_type: string;
  audience_tag_ids?: number[];
  manual_contact_ids?: number[];
  excluded_contact_ids?: number[];
}
export interface AudienceContact {
  id: number;
  name: string;
  email: string;
  do_not_email?: boolean;
  reason?: string;
}
export interface AudiencePreview {
  review_token: string;
  count: number;
  sample: AudienceContact[];
  excluded_count: number;
  excluded_sample: AudienceContact[];
  exclusion_reasons: Record<string, number>;
  quota: EmailQuota;
  frozen_count: number | null;
  accepted_count: number;
  outstanding_count: number;
  held_count: number;
  content: {
    subject: string;
    html: string;
    preview_text: string;
    frozen_at?: string;
  };
}
export interface SendOutcome {
  id: string;
  email: string;
  contact_name: string;
  status: string;
  error: string;
  attempts: number;
  claimed_at: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  quota_reserved: boolean;
  reconciliation_note: string;
}
export const emailError = (error: unknown) =>
  error instanceof Error ? error.message : "The request failed. Please retry.";
export const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const normalizeCampaign = (
  campaign: Partial<EmailCampaign>,
): Partial<EmailCampaign> => ({
  ...campaign,
  audience_tags: campaign.audience_tag_ids ?? campaign.audience_tags ?? [],
  manual_contacts:
    campaign.manual_contact_ids ?? campaign.manual_contacts ?? [],
  excluded_contacts:
    campaign.excluded_contact_ids ?? campaign.excluded_contacts ?? [],
});
export const campaignPayload = (campaign: Partial<EmailCampaign>) => ({
  name: campaign.name,
  template: campaign.template,
  subject: campaign.subject,
  html_content: campaign.html_content,
  preview_text: campaign.preview_text,
  from_name: campaign.from_name,
  reply_to: campaign.reply_to,
  audience_type: campaign.audience_type,
  audience_tags: campaign.audience_tags || [],
  audience_contact_type: campaign.audience_contact_type,
  audience_segment: campaign.audience_segment || null,
  manual_contacts: campaign.manual_contacts || [],
  excluded_contacts: campaign.excluded_contacts || [],
});
export async function allEmailRows<T>(endpoint: string): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; ; page++) {
    const response = await api.get<EmailPage<T> | T[]>(
      `${endpoint}${endpoint.includes("?") ? "&" : "?"}page=${page}`,
    );
    if (Array.isArray(response.data)) return [...rows, ...response.data];
    rows.push(...response.data.results);
    if (!response.data.next) return rows;
    if (!response.data.results.length)
      throw new Error("The list could not be completely loaded. Please retry.");
  }
}
